/**
 * Utility functions for detecting and handling different media types
 */

const VIDEO_EXTENSIONS = ['mp4', 'webm', 'mov', 'avi', 'mkv', 'm4v', 'ogg', 'ogv'];
const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg', 'heic', 'heif', 'avif'];

/**
 * Check if a URL points to a video file
 */
export const isVideoUrl = (url: string): boolean => {
  if (!url) return false;
  
  // Extract the path without query parameters
  const urlWithoutQuery = url.split('?')[0].toLowerCase();
  
  // Check file extension
  const extension = urlWithoutQuery.split('.').pop();
  if (extension && VIDEO_EXTENSIONS.includes(extension)) {
    return true;
  }
  
  return false;
};

/**
 * Check if a URL points to an image file
 */
export const isImageUrl = (url: string): boolean => {
  if (!url) return false;
  
  // Extract the path without query parameters
  const urlWithoutQuery = url.split('?')[0].toLowerCase();
  
  // Check file extension
  const extension = urlWithoutQuery.split('.').pop();
  if (extension && IMAGE_EXTENSIONS.includes(extension)) {
    return true;
  }
  
  // Default to image if no recognized extension (for backwards compatibility)
  return true;
};

/**
 * Get the media type from a URL
 */
export const getMediaType = (url: string): 'video' | 'image' => {
  return isVideoUrl(url) ? 'video' : 'image';
};

/**
 * Get accepted file types for media upload
 */
export const ACCEPTED_MEDIA_TYPES = {
  image: 'image/*',
  video: 'video/mp4,video/webm,video/quicktime',
  all: 'image/*,video/mp4,video/webm,video/quicktime',
};

/**
 * Check if a file is a valid media file for upload
 */
export const isValidMediaFile = (file: File): { valid: boolean; type: 'image' | 'video' | null; error?: string } => {
  if (file.type.startsWith('image/')) {
    return { valid: true, type: 'image' };
  }
  
  if (file.type.startsWith('video/')) {
    // Check for supported video formats
    const supportedVideoTypes = ['video/mp4', 'video/webm', 'video/quicktime'];
    if (supportedVideoTypes.includes(file.type)) {
      return { valid: true, type: 'video' };
    }
    return { valid: false, type: null, error: 'Unsupported video format. Use MP4, WebM, or MOV.' };
  }
  
  return { valid: false, type: null, error: 'File must be an image or video' };
};

/**
 * Maximum file sizes for different media types
 */
export const MAX_FILE_SIZES = {
  image: 5 * 1024 * 1024, // 5MB
  video: 100 * 1024 * 1024, // 100MB
};

/**
 * Get the maximum file size for a given file type
 */
export const getMaxFileSize = (file: File): number => {
  if (file.type.startsWith('video/')) {
    return MAX_FILE_SIZES.video;
  }
  return MAX_FILE_SIZES.image;
};

/**
 * Format file size for display
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Detect actual image kind by reading magic-number bytes.
 * Extensions and MIME types lie (especially iPhone HEIC saved as .jpg).
 */
export type ImageKind = 'jpeg' | 'png' | 'webp' | 'gif' | 'heic' | 'unknown';

export const detectImageKind = async (file: File): Promise<ImageKind> => {
  try {
    const buf = new Uint8Array(await file.slice(0, 32).arrayBuffer());
    if (buf.length < 12) return 'unknown';
    // JPEG: FF D8 FF
    if (buf[0] === 0xff && buf[1] === 0xd8 && buf[2] === 0xff) return 'jpeg';
    // PNG: 89 50 4E 47
    if (buf[0] === 0x89 && buf[1] === 0x50 && buf[2] === 0x4e && buf[3] === 0x47) return 'png';
    // GIF: 47 49 46 38
    if (buf[0] === 0x47 && buf[1] === 0x49 && buf[2] === 0x46 && buf[3] === 0x38) return 'gif';
    // RIFF....WEBP
    if (buf[0] === 0x52 && buf[1] === 0x49 && buf[2] === 0x46 && buf[3] === 0x46 &&
        buf[8] === 0x57 && buf[9] === 0x45 && buf[10] === 0x42 && buf[11] === 0x50) return 'webp';
    // ISO-BMFF: bytes 4-7 = "ftyp", brand at 8-11
    if (buf[4] === 0x66 && buf[5] === 0x74 && buf[6] === 0x79 && buf[7] === 0x70) {
      const brand = String.fromCharCode(buf[8], buf[9], buf[10], buf[11]);
      if (['heic', 'heix', 'hevc', 'hevx', 'mif1', 'msf1', 'heim', 'heis'].includes(brand)) {
        return 'heic';
      }
    }
    return 'unknown';
  } catch {
    return 'unknown';
  }
};

/**
 * Convert an HEIC/HEIF file to JPEG in the browser.
 * heic2any is dynamically imported so non-HEIC uploads pay no bundle cost.
 */
export const convertHeicToJpeg = async (file: File, quality = 0.9): Promise<File> => {
  const heic2any = (await import('heic2any')).default;
  const result = await heic2any({ blob: file, toType: 'image/jpeg', quality });
  const blob = Array.isArray(result) ? result[0] : result;
  const baseName = file.name.replace(/\.(heic|heif|jpe?g|png)$/i, '');
  return new File([blob], `${baseName}.jpg`, { type: 'image/jpeg', lastModified: Date.now() });
};
